import { Manifestacao } from "@/types/administracao/ouvidoria/manifestacao";
import { Service } from "../mainService";
import { PortalError } from "@/types/portal-error";
import { FiltrosManifestacoes } from "@/types/administracao/ouvidoria/filtros-manifestacoes";
import { HistoricoManifestacao } from "@/types/administracao/ouvidoria/historico-manifestacao";
import { AssuntoManifestacao } from "@/types/administracao/ouvidoria/assunto-manifestacao";
import { StatusManifestacao } from "@/types/administracao/ouvidoria/status-manifestacao";
import { ProrrogacaoOuvidoria } from "@/types/administracao/ouvidoria/prorrogacao-ouvidoria";
import { ArquivosManifestacao } from "@/types/administracao/ouvidoria/arquivos-manifestacao";
import { Andamento } from "@/types/administracao/ouvidoria/andamento";
import { AndamentoJson } from "@/types/administracao/ouvidoria/andamento";

const ouvidoriaService = {
  adicionarAndamento,
  baixarArquivoManifestacao,
  concluirManifestacao,
  filtraManifestacoes,
  getManifestacaoById,
  getAllAssunto,
  getAllStatus,
  getProrrogacoesByManifestacaoId,
  getAllArquivosByManifestacaoId,
  prorrogarManifestacao,
  listarAndamentos,
  gerarProtocolo,
};

async function filtraManifestacoes(
  page: number,
  filtros: FiltrosManifestacoes
): Promise<{ totalRows: number; manifestacoes: Manifestacao[] }> {
  try {
    const response = await Service.$http.post(
      `${process.env.VUE_APP_MESSAGES_API}/Ouvidoria/filtrarManifestacoes?page=${page}&pageSize=10`,
      filtros.toFormData()
    );
    return {
      totalRows: await response.data.totalRows,
      manifestacoes: await response.data.manifest.map((manifest: any) => new Manifestacao(manifest)),
    };
  } catch (e: any) {
    throw new PortalError(e.response?.data?.erros ?? e.response?.data?.msg ?? e.message);
  }
}

async function getManifestacaoById(
  idManifestacao: number
): Promise<Manifestacao> {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_MESSAGES_API}/Ouvidoria/detalhes/${idManifestacao}`
    );
    const manifestacao = new Manifestacao(await response.data.manifestacao);
    manifestacao.HistoricoManifests = await response.data.historicos.map(
      (hist: any) => new HistoricoManifestacao(hist)
    );
    return manifestacao;
  } catch (e: any) {
    throw new PortalError(e.response?.data?.erros ?? e.response?.data?.msg ?? e.message);
  }
}


async function getAllAssunto(): Promise<AssuntoManifestacao[]> {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_MESSAGES_API}/Ouvidoria/allAssuntoManifestacao`
    );
    return await response.data;
  } catch (e: any) {
    throw new PortalError(e.response?.data?.erros ?? e.response?.data?.msg ?? e.message);
  }
}

async function getAllStatus(): Promise<StatusManifestacao[]> {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_MESSAGES_API}/Ouvidoria/allStatusManifestacao`
    );
    return response.data;
  } catch (e: any) {
    throw new PortalError(e.response?.data?.erros ?? e.response?.data?.msg ?? e.message);
  }
}

async function prorrogarManifestacao(
  idManifestacao: number,
  justificativa: string,
  novaData: string
): Promise<any> {
  try {
    const data = new FormData();
    data.append("justificativa", justificativa);
    data.append("novaData", String(novaData));
    const response = await Service.$http.post(
      `${process.env.VUE_APP_MESSAGES_API}/Ouvidoria/adicionarProrrogacao/${idManifestacao}`,
      data
    );
    return response;
  } catch (e: any) {
    throw new PortalError(e.response?.data?.erros ?? e.response?.data?.msg ?? e.message);
  }
}

async function getProrrogacoesByManifestacaoId(
  idManifestacao: number
): Promise<ProrrogacaoOuvidoria[]> {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_MESSAGES_API}/Ouvidoria/prorrogacaoManifestacao?idManifestacao=${idManifestacao}`
    );
    return await response.data;
  } catch (e: any) {
    throw new PortalError(e.response?.data?.erros ?? e.response?.data?.msg ?? e.message);
  }
}

async function concluirManifestacao(idManifestacao: number): Promise<any> {
  try {
    const response = await Service.$http.post(
      `${process.env.VUE_APP_MESSAGES_API}/Ouvidoria/concluirManifestacao/${idManifestacao}`
    );
    return await response;
  } catch (e: any) {
    throw new PortalError(e.response?.data?.erros ?? e.response?.data?.msg ?? e.message);
  }
}

async function getAllArquivosByManifestacaoId(
  idManifestacao: number
): Promise<ArquivosManifestacao[]> {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_MESSAGES_API}/Ouvidoria/arquivosManifestacao?idManifestacao=${idManifestacao}`
    );
    return await response.data;
  } catch (e: any) {
    throw new PortalError(e.response?.data?.erros ?? e.response?.data?.msg ?? e.message);
  }
}

async function baixarArquivoManifestacao(idArquivo: number): Promise<any> {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_MESSAGES_API}/Ouvidoria/manifestacao/arquivo/${idArquivo}/download`,
      { responseType: "blob" }
    );
    return response;
  } catch (e: any) {
    let data = e.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        data = JSON.parse(text);
      } catch {
        data = { msg: text };
      }
    }
    throw new PortalError(data.erros ?? data.msg ?? e.message);
  }
}

async function listarAndamentos(idManifestacao: number): Promise<Andamento[]> {
  const response = await Service.$http.get(
    `${process.env.VUE_APP_MESSAGES_API}/Ouvidoria/andamentoManifestacao?idManifestacao=${idManifestacao}`
  )
  if (response && response.status === 200) return response.data.map((andamento) => new Andamento(andamento))
  throw new PortalError('Erro ao obter andamentos da manifestação')
}

async function adicionarAndamento(idManifestacao: number, tipo: string, descricao: string) {
  const data = new FormData();
  data.append("tipo", tipo);
  data.append("descricao", descricao);

  const response = await Service.$http.post(
    `${process.env.VUE_APP_MESSAGES_API}/Ouvidoria/adicionarAndamento/${idManifestacao}`, data
  )
  if (response && response.status === 200) return response
  throw new PortalError('Erro ao adicionar andamento a manifestação')
}

async function gerarProtocolo(idManifestacao: number): Promise<any> {
  try {
    const response = await Service.$http.post(
      `${process.env.VUE_APP_MESSAGES_API}/Ouvidoria/gerarProtocoloOuvidoria/${idManifestacao}`
    );
    return await response;
  } catch (e: any) {
    throw new PortalError(e.response?.data?.erros ?? e.response?.data?.msg ?? e.message ?? "Erro ao gerar protocolo");
  }
}

export default ouvidoriaService;

